import {
  isSubscriptionRequiredBody,
  ORG_HEADER,
  SUBSCRIPTION_REQUIRED_EVENT,
} from "../../features/subscription/subscription.utils";
import { isApiUrl } from "../../features/twoStep/twoStep.utils";
import { getLinkedFirm } from "../../sessionStorage/sessionStorage";

/**
 * Seat billing (2026-10-24): every call to our API says which firm it is for
 * (X-Proc-Org = the firm picked at sign-in), so the server's lock applies to
 * the right firm for people in several firms. A 402
 * {code:"subscription_required"} answer fires SUBSCRIPTION_REQUIRED_EVENT so
 * the app switches to the "Add a payment method" page. Same approach as
 * installTwoStepHeader (fetch + XMLHttpRequest, our API only).
 */

let installed = false;

const currentOrgId = (): string => {
  try {
    const firm = getLinkedFirm();
    return typeof firm?.orgId === "string" ? firm.orgId : "";
  } catch {
    return "";
  }
};

const notify = () => {
  try {
    window.dispatchEvent(new Event(SUBSCRIPTION_REQUIRED_EVENT));
  } catch {
    // very old browsers: ignore
  }
};

const urlOf = (input: RequestInfo | URL): string => {
  if (typeof input === "string") return input;
  if (input instanceof URL) return input.href;
  return (input as Request).url;
};

export const installFirmHeader = (apiBase: string) => {
  if (installed || typeof window === "undefined") return;
  installed = true;

  const originalFetch = window.fetch?.bind(window);
  if (originalFetch) {
    window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
      const url = urlOf(input);
      if (!isApiUrl(url, apiBase)) return originalFetch(input, init);
      const orgId = currentOrgId();
      let nextInit = init;
      if (orgId) {
        const headers = new Headers(
          init?.headers ||
            (input instanceof Request ? input.headers : undefined),
        );
        if (!headers.has(ORG_HEADER)) headers.set(ORG_HEADER, orgId);
        nextInit = { ...(init || {}), headers };
      }
      const response = await originalFetch(input, nextInit);
      if (response.status === 402) {
        response
          .clone()
          .text()
          .then((text) => {
            if (isSubscriptionRequiredBody(text)) notify();
          })
          .catch(() => undefined);
      }
      return response;
    };
  }

  const XHR = window.XMLHttpRequest;
  if (XHR?.prototype) {
    const open = XHR.prototype.open;
    const send = XHR.prototype.send;
    type Tagged = XMLHttpRequest & { __procFirmApi?: boolean };
    XHR.prototype.open = function (
      this: Tagged,
      method: string,
      url: string | URL,
      ...rest: unknown[]
    ) {
      this.__procFirmApi = isApiUrl(String(url), apiBase);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return (open as any).call(this, method, url, ...rest);
    } as typeof XHR.prototype.open;
    XHR.prototype.send = function (
      this: Tagged,
      body?: Document | XMLHttpRequestBodyInit | null,
    ) {
      if (this.__procFirmApi) {
        const orgId = currentOrgId();
        if (orgId) {
          try {
            this.setRequestHeader(ORG_HEADER, orgId);
          } catch {
            // already sent / not opened: leave it
          }
        }
        this.addEventListener("loadend", () => {
          if (this.status !== 402) return;
          try {
            const text =
              this.responseType === "" || this.responseType === "text"
                ? this.responseText
                : this.responseType === "json"
                ? this.response
                : "";
            if (isSubscriptionRequiredBody(text)) notify();
          } catch {
            // unreadable body: ignore
          }
        });
      }
      return send.call(this, body);
    };
  }
};
